/**
 * Materials module for Design mode — ported from Designer
 * Material loading, waterfall rendering, and add-to-design
 */

import { store } from '../../../core/store.js'
import { CONFIG, getCdnThumbnail } from '../../../core/config.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'
import { getSessionId, apiRequest } from '../../../api/index.js'
import { addMaterialToDesign, urlToFile, convertToSDSUrlViaWorker } from './add-to-design.js'

const log = createLogger('materials')

// Module state
const state = {
  materials: [],
  usedMaterials: [],
  loading: false,
  hasMore: true,
  offset: 0,
  columns: 2,
  columnHeights: [0, 0],
  scrollListener: null,
}

// ============================================================
// Infinite Scroll
// ============================================================

function setupInfiniteScroll() {
  const grid = document.getElementById('materialsGrid')
  if (!grid) return

  if (state.scrollListener) {
    grid.removeEventListener('scroll', state.scrollListener)
  }

  let ticking = false
  state.scrollListener = () => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      ticking = false
      if (state.loading || !state.hasMore) return

      const { scrollTop, scrollHeight, clientHeight } = grid
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        log.info('Reached scroll threshold, loading more...')
        loadMaterials(true)
      }
    })
  }

  grid.addEventListener('scroll', state.scrollListener, { passive: true })
}

function createSkeleton() {
  const ratios = ['3/4', '4/5', '1/1', '5/4', '4/3']
  const ratio = ratios[Math.floor(Math.random() * ratios.length)]
  return `<div class="material-item skeleton-container">
    <div class="skeleton skeleton-image" style="aspect-ratio:${ratio}"></div>
  </div>`
}

// ============================================================
// Load Materials
// ============================================================

async function loadMaterials(append = false) {
  if (state.loading) return
  if (!append && state.offset > 0) return
  if (append && !state.hasMore) return

  if (!append) {
    state.offset = 0
    state.hasMore = true
    state.columnHeights = [0, 0]
  }

  state.loading = true
  const grid = document.getElementById('materialsGrid')
  const sessionId = getSessionId()

  if (!append && grid) {
    grid.innerHTML = `
      <div class="waterfall-container">
        <div class="waterfall-column">${createSkeleton().repeat(3)}</div>
        <div class="waterfall-column">${createSkeleton().repeat(3)}</div>
      </div>
    `
  } else if (append && grid) {
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'loading-indicator'
    loadingDiv.id = 'loadingMore'
    loadingDiv.innerHTML =
      '<div class="spinner"></div><div style="margin-top:8px;">Loading more...</div>'
    grid.appendChild(loadingDiv)
  }

  try {
    // Arts use the canonical creator-api curated image feed, the same source
    // as gallery. User uploads are excluded from this anonymous library.
    const url = `${CONFIG.FRONTEND_API}/api/images?scene=gallery&limit=20&offset=${state.offset}&session_id=${sessionId}`
    log.info('Fetching materials...', { offset: state.offset, append })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000)

    const response = await fetch(url, {
      headers: { 'X-Session-ID': sessionId },
      signal: controller.signal,
    })
    clearTimeout(timeoutId)

    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const result = await response.json()
    const items = result.images || result.recommendations

    if (result.success && items?.length > 0) {
      let images = items
      const apiCount = images.length

      // Insert recently used materials from localStorage (first load only)
      if (!append) {
        try {
          const recent = JSON.parse(localStorage.getItem('mms_recent_materials') || '[]')
          if (recent.length > 0) {
            const recentWithReason = recent
              .slice(0, 3)
              .map((m) => ({ ...m, reason: 'recently-used' }))
            const existingIds = new Set(images.map((m) => String(m.id)))
            const uniqueRecent = recentWithReason.filter((m) => !existingIds.has(String(m.id)))
            if (uniqueRecent.length > 0) {
              images = [...uniqueRecent, ...images]
            }
          }
        } catch (e) {
          /* ignore */
        }
      }

      state.materials = append ? state.materials.concat(images) : images
      state.offset += apiCount
      state.hasMore = result.has_more !== false && apiCount >= 20

      log.info(`Loaded ${images.length} images (total: ${state.materials.length})`)

      const loadingMore = document.getElementById('loadingMore')
      if (loadingMore) loadingMore.remove()

      renderMaterials(append)
      // H-140: design-entry application (design_material param) hangs off the
      // first-page load — emitted for appends too so a late flush can retry.
      eventBus.emit(EVENTS.MATERIALS_LOADED, { append })

      if (state.hasMore && state.materials.length - state.offset < 10) {
        setTimeout(() => loadMaterials(true), 1000)
      }

      return state.materials
    } else {
      state.hasMore = false
      if (!append && grid) {
        grid.innerHTML = '<div class="placeholder-text">No images available</div>'
      }
      return []
    }
  } catch (error) {
    log.error('Failed to load materials:', error)

    const isTimeout = error.name === 'AbortError'
    const errorMessage = isTimeout
      ? 'Request timeout. Please check your connection.'
      : 'Failed to load images'

    if (grid) {
      grid.innerHTML = `<div class="placeholder-text" style="color: var(--state-danger);">
        <div>${errorMessage}</div>
        <button class="design-start-btn" style="margin-top:12px;" id="materialsRetryBtn">Retry</button>
      </div>`
      const retryBtn = document.getElementById('materialsRetryBtn')
      if (retryBtn)
        retryBtn.onclick = () => {
          state.loading = false
          loadMaterials(false)
        }
    }

    if (isTimeout && !append) {
      setTimeout(() => {
        state.loading = false
        loadMaterials(false)
      }, 2000)
    }

    return []
  } finally {
    state.loading = false
  }
}

// ============================================================
// Render
// ============================================================

function getRecommendLabel(reason) {
  const labels = {
    personalized: 'FOR YOU',
    trending: 'TRENDING',
    similar: 'SIMILAR',
    explore: 'NEW',
    popular: 'POPULAR',
    'recent-favorite': 'FAVORITE',
    'recently-used': 'RECENT',
    'chrome-extension': 'IMPORTED',
  }
  return labels[reason] || 'REC'
}

function renderMaterials(append = false) {
  const grid = document.getElementById('materialsGrid')
  if (!grid) return

  if (!append) {
    grid.innerHTML = ''
    const container = document.createElement('div')
    container.className = 'waterfall-container'
    container.id = 'materialsWaterfallContainer'

    for (let i = 0; i < state.columns; i++) {
      const column = document.createElement('div')
      column.className = 'waterfall-column'
      column.id = `material-col-${i}`
      container.appendChild(column)
    }

    grid.appendChild(container)
    state.columnHeights = new Array(state.columns).fill(0)
  }

  const startIdx = append ? state.materials.length - (state.offset > 20 ? 20 : state.offset) : 0

  for (let idx = startIdx; idx < state.materials.length; idx++) {
    const material = state.materials[idx]
    // Build thumbs from cloudflare_id via the live delivery base — raw
    // thumbnail_url/url values can still carry retired-host URLs until the
    // hourly url-migration cron has swept them (ADR-006 DEBT).
    const thumbnailUrl = getCdnThumbnail(material)
    const originalUrl = material.cloudflare_id
      ? `${CONFIG.IMAGE_CDN}/${material.cloudflare_id}/public`
      : material.url

    const item = document.createElement('div')
    item.className = 'material-item'
    item.style.position = 'relative'
    // H-140: the entry-selected marker (design_material param) locates cards
    // by material id.
    item.dataset.matId = String(material.id)

    item.onclick = (e) => {
      if (e.target.closest('.fav-toggle-btn')) return
      // A manual pick supersedes the entry-preselected highlight.
      clearEntrySelection()
      // Pass intrinsic size when the material carries it so the picture_request
      // gate (sds-data-model.md section 6) skips the extra image load.
      addMaterialToDesign(originalUrl, material.id, material.title, {
        width: material.width,
        height: material.height,
      })
    }

    if (material.reason) {
      const badge = document.createElement('div')
      badge.className = 'rec-badge'
      badge.textContent = getRecommendLabel(material.reason)
      item.appendChild(badge)
    }

    // Favorite button
    const favBtn = document.createElement('button')
    favBtn.className = 'fav-toggle-btn'
    const isFav = window.FavoritesManager?.isFavorite?.(material.id) || false
    if (isFav) favBtn.classList.add('active')
    favBtn.innerHTML = `<i class="ph ph-heart${isFav ? '-fill' : ''}" style="font-size:14px;"></i>`
    favBtn.onclick = (e) => {
      e.stopPropagation()
      if (window.FavoritesManager?.toggleFavorite) {
        window.FavoritesManager.toggleFavorite(material).then((added) => {
          favBtn.classList.toggle('active', added)
          favBtn.innerHTML = `<i class="ph ph-heart${added ? '-fill' : ''}" style="font-size:14px;"></i>`
          eventBus.emit(EVENTS.TOAST_SHOW, {
            message: added ? 'Added to favorites' : 'Removed from favorites',
            type: added ? 'success' : 'info',
            duration: 1500,
          })
        })
      }
    }
    item.appendChild(favBtn)

    const img = document.createElement('img')
    img.src = thumbnailUrl
    img.loading = idx < 12 ? 'eager' : 'lazy'
    img.alt = material.title || 'Material'
    img.onerror = function () {
      this.src =
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="200"%3E%3Crect fill="%231e2228" width="150" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%237a818c" font-family="Arial" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E'
    }
    item.appendChild(img)

    const shortestColIndex = state.columnHeights.indexOf(Math.min(...state.columnHeights))
    const column = document.getElementById(`material-col-${shortestColIndex}`)

    if (column) {
      column.appendChild(item)
      state.columnHeights[shortestColIndex] += 200 + 12
    }
  }

  log.info(`Rendered ${state.materials.length} materials`)
}

// ============================================================
// Init on SDK ready
// ============================================================

function init() {
  log.info('Materials module initialized')
  setupInfiniteScroll()
}

// Load materials when the design SDK is ready — but ONLY while Design mode is
// actually active. The boot preload fires SDK_READY on EVERY visit (create /
// gallery / phone included), and the eager fetch here was burning a request +
// logging "Failed to load materials" noise for visitors who never open
// Design. why → mobile review 2026-07-07.
eventBus.on(EVENTS.SDK_READY, () => {
  if (store.getState('mode') === 'design' && state.materials.length === 0) {
    loadMaterials(false)
  }
})

/**
 * Idempotent activation hook — Design mode calls this on activate so the
 * panel fills on first entry even when SDK_READY fired earlier (boot preload)
 * while another mode was active.
 */
export function ensureMaterialsLoaded() {
  if (state.materials.length === 0 && !state.loading) {
    loadMaterials(false)
  }
}

// ============================================================
// H-140 entry selection (design_material / Create "use this design" exit)
// ============================================================

/** Remove the entry-preselected highlight from every material card. */
function clearEntrySelection() {
  document
    .querySelectorAll('#materialsGrid .material-item.entry-selected')
    .forEach((el) => el.classList.remove('entry-selected'))
}

/**
 * Locate + preselect a material at the head of the Arts panel (H-140).
 * Locate/preselect ONLY — never auto-injects the canvas (task-card red line).
 *
 * Resolution order: an existing loaded row (by id, then url) → a fetch of the
 * images-table row when only an id is known → a synthesized entry row when
 * only a url is known (the Create exit carries a fresh, not-yet-cataloged
 * material). The material is deduped to the head and the list re-rendered,
 * then the card is marked + scrolled into view and the Arts tab raised.
 *
 * @param {{ id?: string|number|null, url?: string, title?: string }} target
 * @returns {Promise<boolean>} true when the material was located and marked.
 */
export async function selectArtsMaterial(target) {
  if (!target || (target.id == null && !target.url)) return false

  let material = null
  if (target.id != null) {
    material = state.materials.find((m) => String(m.id) === String(target.id)) || null
  }
  if (!material && target.url) {
    material = state.materials.find((m) => m.url === target.url) || null
  }
  if (!material && target.id != null) {
    try {
      const res = await apiRequest(
        `${CONFIG.FRONTEND_API}/api/images/${encodeURIComponent(target.id)}`
      )
      const img = res?.data
      if (img && (img.url || img.url_thumb)) material = img
    } catch (err) {
      log.warn('Entry material fetch failed:', err?.message || err)
    }
  }
  if (!material && target.url) {
    material = { id: `entry-${Date.now()}`, url: target.url, title: target.title }
  }
  if (!material) return false

  // Head-insert (deduped) so the entry material is the Arts first position.
  state.materials = [
    material,
    ...state.materials.filter((m) => String(m.id) !== String(material.id)),
  ]
  renderMaterials(false)

  requestAnimationFrame(() => {
    const grid = document.getElementById('materialsGrid')
    if (!grid) return
    clearEntrySelection()
    const card = grid.querySelector(`[data-mat-id="${CSS.escape(String(material.id))}"]`)
    if (!card) return
    card.classList.add('entry-selected')
    // The Arts tab must be the visible left panel for the mark to be seen.
    const artsTab = document.querySelector('#sds-container .design-tab[data-design-tab="arts"]')
    if (artsTab && !artsTab.classList.contains('active')) artsTab.click()
    card.scrollIntoView({ block: 'nearest' })
  })
  return true
}

// Expose global
if (typeof window !== 'undefined') {
  window.MaterialManager = {
    state,
    init,
    loadMaterials,
    renderMaterials,
    addMaterialToDesign,
    urlToFile,
    convertToSDSUrlViaWorker,
    selectArtsMaterial,
  }
}

export { init, loadMaterials, renderMaterials, addMaterialToDesign, state }

export default {
  init,
  loadMaterials,
  renderMaterials,
  addMaterialToDesign,
  state,
}
